import * as THREE from "three";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { updateCurrentTransformState } from "../../redux/objectPlacement";
import { useDispatch } from "react-redux";
import {
  Y_COSMETIC_LIFT_AMOUNT,
  getMouseTargetPosition,
  updateConnectionLinePosition,
  updateGroundPosition,
  updateSpherePosition,
  updateXMarkPosition,
  Y_LIFT_AMOUNT,
  objectSelfPartKeyAndProp,
} from "./utils";
import { isEventAClick } from "../utils";
import { useOrbit } from "../CustomHooks";

export const useUpdateMesh = (meshRef, position, rotation, scale) => {
  useEffect(() => {
    if (meshRef.current && position) {
      meshRef.current.position.set(position[0], position[1], position[2]);
    }
  }, [position]);

  useEffect(() => {
    if (meshRef.current && rotation) {
      meshRef.current.rotation.set(rotation[0], rotation[1], rotation[2]);
    }
  }, [rotation]);

  useEffect(() => {
    if (meshRef.current && scale) {
      meshRef.current.scale.set(scale[0], scale[1], scale[2]);
    }
  }, [scale]);
};

export const useUpdateMeshState = (meshRef) => {
  const dispatch = useDispatch();
  const lastStateRef = useRef(null);

  useFrame(() => {
    if (meshRef.current) {
      const currentState = {
        position: meshRef.current.position.toArray(),
        rotation: meshRef.current.rotation.toArray().slice(0, 3),
        scale: meshRef.current.scale.toArray(),
      };

      if (
        !lastStateRef.current ||
        !arraysEqual(lastStateRef.current.position, currentState.position) ||
        !arraysEqual(lastStateRef.current.rotation, currentState.rotation) ||
        !arraysEqual(lastStateRef.current.scale, currentState.scale)
      ) {
        dispatch(updateCurrentTransformState(currentState));
        lastStateRef.current = currentState;
      }
    }
  });
};

const arraysEqual = (a, b, tolerance = 0.0001) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Math.abs(a[i] - b[i]) > tolerance) return false;
  }
  return true;
};

export const useSetupTransformControls = (
  meshRef,
  transformRef,
  transformMode,
  transformAxis
) => {
  const { toggleOrbit } = useOrbit();

  useEffect(() => {
    if (transformMode === "rotate" && transformAxis === "xyz") {
      const controls = transformRef.current;
      const mesh = meshRef.current;
      if (!controls || !mesh) return;

      controls.attach(mesh);

      controls.addEventListener("dragging-changed", toggleOrbit);
      return () => {
        controls.removeEventListener("dragging-changed", toggleOrbit);
        controls.detach();
      };
    }
  }, [transformMode, transformAxis, toggleOrbit]);
};

export const useHide4thRotationCircle = (transformRef) => {
  useFrame(() => {
    const controls = transformRef.current;
    if (!controls) return;

    const eCircle = controls.getObjectByName("E");
    if (eCircle) {
      eCircle.parent.remove(eCircle);
    }
  });
};

export const useTriggereRerender = (meshRef, selectedShape) => {
  const [triggerRerender, setTriggerRerender] = useState({
    inital: false,
    shapeChangeFlag: false,
  });
  useEffect(() => {
    if (meshRef?.current && !triggerRerender.inital) {
      setTriggerRerender((prev) => ({ ...prev, inital: true }));
    }
  }, [meshRef, triggerRerender.inital]);

  useEffect(() => {
    setTriggerRerender((prev) => ({
      ...prev,
      shapeChangeFlag: !prev.shapeChangeFlag,
    }));
  }, [selectedShape]);
};

export const useSetupMouseRotation = (
  meshRef,
  transformMode,
  transformAxis
) => {
  const { gl, camera, setFrameloop } = useThree();

  const { disableOrbit, enableOrbit } = useOrbit();

  const isDragging = useRef(false);
  const startMousePos = useRef(new THREE.Vector2());

  useEffect(() => {
    if (
      transformMode === "rotate" &&
      transformAxis === "mouse" &&
      meshRef.current
    ) {
      const mesh = meshRef.current;
      let startQuaternion = new THREE.Quaternion();
      let meshCenter = new THREE.Vector3();

      const handleMouseDown = (event) => {
        const rect = gl.domElement.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        mesh.getWorldPosition(meshCenter);

        const meshScreenPos = meshCenter.clone().project(camera);
        const meshScreenX = (meshScreenPos.x * 0.5 + 0.5) * rect.width;
        const meshScreenY = (-meshScreenPos.y * 0.5 + 0.5) * rect.height;

        const distanceToCenter = Math.sqrt(
          Math.pow(mouseX - meshScreenX, 2) + Math.pow(mouseY - meshScreenY, 2)
        );

        const clickRadius = 100;

        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2(
          ((event.clientX - rect.left) / rect.width) * 2 - 1,
          -((event.clientY - rect.top) / rect.height) * 2 + 1
        );
        raycaster.setFromCamera(mouse, camera);
        raycaster.params.Points.threshold = 0.1;
        raycaster.params.Line.threshold = 0.1;
        const intersects = raycaster.intersectObject(mesh, true);

        if (intersects.length > 0 || distanceToCenter < clickRadius) {
          isDragging.current = true;
          startMousePos.current.set(event.clientX, event.clientY);
          startQuaternion.copy(mesh.quaternion);

          disableOrbit();
          setFrameloop("always");
          gl.domElement.style.cursor = "grabbing";

          event.preventDefault();
          event.stopPropagation();
        }
      };

      const handleMouseMove = (event) => {
        if (!isDragging.current) return;

        const currentMousePos = new THREE.Vector2(event.clientX, event.clientY);

        const rect = gl.domElement.getBoundingClientRect();

        const meshScreenPos = meshCenter.clone().project(camera);
        const meshScreenX = (meshScreenPos.x * 0.5 + 0.5) * rect.width;
        const meshScreenY = (-meshScreenPos.y * 0.5 + 0.5) * rect.height;

        const startAngle = Math.atan2(
          startMousePos.current.y - meshScreenY,
          startMousePos.current.x - meshScreenX
        );
        const currentAngle = Math.atan2(
          currentMousePos.y - meshScreenY,
          currentMousePos.x - meshScreenX
        );

        let rotationAngle = currentAngle - startAngle;

        const cameraDirection = new THREE.Vector3();
        camera.getWorldDirection(cameraDirection);

        const rotationQuaternion = new THREE.Quaternion();
        rotationQuaternion.setFromAxisAngle(cameraDirection, rotationAngle);

        const newQuaternion = new THREE.Quaternion();
        newQuaternion.multiplyQuaternions(rotationQuaternion, startQuaternion);

        mesh.setRotationFromQuaternion(newQuaternion);

        event.preventDefault();
      };

      const handleMouseUp = (event) => {
        if (isDragging.current) {
          isDragging.current = false;
          enableOrbit();
          setFrameloop("demand");
          gl.domElement.style.cursor = "auto";
        }
      };

      gl.domElement.addEventListener("mousedown", handleMouseDown);
      gl.domElement.addEventListener("mousemove", handleMouseMove);
      gl.domElement.addEventListener("mouseup", handleMouseUp);
      gl.domElement.addEventListener("mouseleave", handleMouseUp);

      return () => {
        gl.domElement.removeEventListener("mousedown", handleMouseDown);
        gl.domElement.removeEventListener("mousemove", handleMouseMove);
        gl.domElement.removeEventListener("mouseup", handleMouseUp);
        gl.domElement.removeEventListener("mouseleave", handleMouseUp);
      };
    }
  }, [
    transformMode,
    transformAxis,
    camera,
    gl.domElement,
    setFrameloop,
    disableOrbit,
    enableOrbit,
  ]);
};

export const useHandleMouseEnterAndLeave = ({ setIsMouseInCanvas }) => {
  useEffect(() => {
    const canvas = document.querySelector("canvas");

    const handleMouseEnter = () => {
      setIsMouseInCanvas(true);
    };
    const handleMouseLeave = () => {
      setIsMouseInCanvas(false);
    };

    if (canvas) {
      canvas.addEventListener("mouseenter", handleMouseEnter);
      canvas.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        canvas.removeEventListener("mouseenter", handleMouseEnter);
        canvas.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [setIsMouseInCanvas]);
};

export const animateCameraToPoint = (
  endPosition,
  camera,
  orbitRef,
  onFinish,
  distance = 25
) => {
  const startPosition = camera.position.clone();
  const cameraToTargetDistance = Math.sqrt(
    Math.pow(endPosition.x - startPosition.x, 2) +
      Math.pow(endPosition.y - startPosition.y, 2) +
      Math.pow(endPosition.z - startPosition.z, 2)
  );
  if (cameraToTargetDistance < distance) {
    return;
  }

  if (orbitRef.current) {
    orbitRef.current.target.set(endPosition.x, endPosition.y, endPosition.z);
    orbitRef.current.update();
  }

  const maxDuration = 1 - distance / cameraToTargetDistance;
  const startTime = Date.now();
  const duration = 2000;

  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, maxDuration);

    const easeOut = 1 - Math.pow(1 - progress, 2);

    const currentPosition = startPosition.clone().lerp(endPosition, easeOut);
    camera.position.copy(currentPosition);

    if (easeOut < maxDuration) {
      requestAnimationFrame(animate);
    } else {
      onFinish?.();
    }
  };

  animate();
};

export const usePointPlacer = (
  orbitRef,
  setEditingPosition,
  onPlacePoint,
  liftY,
  isAnimationActive,
  onSpherePositionChange,
  snapPosition
) => {
  const { mouse, camera, raycaster, scene } = useThree();
  const [isMouseInCanvas, setIsMouseInCanvas] = useState(true);
  const [mouseLiftedPosition, setMouseLiftedPosition] = useState([0, 0, 0]);

  const [groundPosition, setGroundPosition] = useState([0, 0, 0]);

  const followingSphereRef = useRef();
  const xMarkRef = useRef();
  const connectionLineRef = useRef();

  const LIFTED_POSITION_Y = liftY ? Y_LIFT_AMOUNT : Y_COSMETIC_LIFT_AMOUNT;
  const DISTANCE_FROM_CAMERA = 10;

  const updateGroundAndSpherePosition = useCallback(() => {
    const targetPosition = getMouseTargetPosition(
      raycaster,
      mouse,
      camera,
      scene,
      followingSphereRef,
      DISTANCE_FROM_CAMERA
    );
    updateGroundPosition(targetPosition, groundPosition, setGroundPosition);
    updateSpherePosition(
      targetPosition,
      followingSphereRef,
      mouseLiftedPosition,
      setMouseLiftedPosition,
      LIFTED_POSITION_Y,
      onSpherePositionChange,
      snapPosition
    );
  }, [
    raycaster,
    mouse,
    camera,
    scene,
    followingSphereRef,
    DISTANCE_FROM_CAMERA,
    groundPosition,
    setGroundPosition,
    mouseLiftedPosition,
    setMouseLiftedPosition,
    LIFTED_POSITION_Y,
    onSpherePositionChange,
    snapPosition,
  ]);

  const handlePlacePoint = useCallback(
    (event) => {
      if (isEventAClick(event)) {
        setEditingPosition([
          mouseLiftedPosition[0],
          mouseLiftedPosition[1],
          mouseLiftedPosition[2],
        ]);

        onPlacePoint?.();

        const targetPoint = new THREE.Vector3(
          mouseLiftedPosition[0],
          mouseLiftedPosition[1],
          mouseLiftedPosition[2]
        );

        if (isAnimationActive)
          animateCameraToPoint(targetPoint, camera, orbitRef);
      }
    },
    [
      setEditingPosition,
      onPlacePoint,
      mouseLiftedPosition,
      camera,
      orbitRef,
      isAnimationActive,
    ]
  );

  const updateAllPositions = useCallback(() => {
    if (
      followingSphereRef.current &&
      xMarkRef.current &&
      connectionLineRef.current
    ) {
      updateGroundAndSpherePosition();
      updateXMarkPosition(xMarkRef, groundPosition);
      updateConnectionLinePosition(
        connectionLineRef,
        followingSphereRef,
        groundPosition
      );
    }
  }, [
    followingSphereRef,
    xMarkRef,
    connectionLineRef,
    groundPosition,
    updateGroundAndSpherePosition,
  ]);

  useFrame(() => {
    if (isMouseInCanvas) updateAllPositions();
  });

  useHandleMouseEnterAndLeave({ setIsMouseInCanvas });

  return {
    handlePlacePoint,
    refs: {
      followingSphereRef,
      xMarkRef,
      connectionLineRef,
    },
    meshProps: objectSelfPartKeyAndProp.prop,
    isVisible: isMouseInCanvas,
  };
};

const getScaleFactor = (camera) => {
  return 0.04 * Math.tan((camera.fov * Math.PI) / 360);
};

export const useScaleSizeUniformly = ({
  referenceObjectRef,
  scaleObjectRef,
  active = true,
  setScaleFactorFn,
  threshold = 1,
}) => {
  const { camera } = useThree();
  useFrame(() => {
    if (referenceObjectRef?.current && active) {
      const scaleFactor = getScaleFactor(camera);

      const distance = camera.position.distanceTo(
        referenceObjectRef.current?.position
      );

      const newScale = scaleFactor * distance;
      if (scaleObjectRef) {
        scaleObjectRef.current.scale.setScalar(newScale);
      }
      if (setScaleFactorFn)
        setScaleFactorFn((prev) =>
          Math.abs(newScale - prev) / prev < threshold ? prev : newScale
        );
    }
  });
};
